import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { CustomerEditBreadcrumbItems } from 'data/commonData';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import CreateStaffForm from 'components/forms/CreateStaffForm';
import { ChangeEvent, FormEvent, useState, useEffect } from 'react';
import {
  CreateCustomerDTO,
  CreateCustomerFormData
} from 'data/from-data/create-customer-form.data';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { GET_CUSTOMER, UPDATE_CUSTOMER } from 'services/api/customer';
import { FormProps } from 'data/from-data/interface/form-props.interface';
import { useNavigate } from 'react-router-dom';
import { Link, useParams } from 'react-router-dom';
import { SITE_ENDPOINT } from 'Constant';
import PhoenixLoader from 'components/common/PhoenixLoader';

const EditCustomer = () => {
  const { id } = useParams<{ id?: string }>();
  const [validated, setValidated] = useState<boolean>(false);
  const [createCustomerDto, setCreateCustomerDto] =
    useState<CreateCustomerFormData>(new CreateCustomerFormData());
  const [isLoading, setLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient(); // Get the QueryClient instance

  const getCustomerQuery = useQuery(['getCustomer', id], () =>
    id ? GET_CUSTOMER(id) : Promise.reject('No id provided')
  );
  const updateCustomerAction = useMutation('updateCustomer', UPDATE_CUSTOMER, {
    onError: (err: any) => {
      console.log(err);
      setLoading(false);
    },
    onSuccess: () => {
      setLoading(false);
      queryClient.invalidateQueries('get_customer');
      queryClient.invalidateQueries('getCustomer');
      navigate(`${SITE_ENDPOINT.BASE}${SITE_ENDPOINT.COMPANY.INDEX}`);
    },
    onMutate: () => {
      setLoading(true);
    }
  });

  useEffect(() => {
    if (getCustomerQuery.isSuccess) {
      const { data: customerData }: any = getCustomerQuery.data;
      setCreateCustomerDto(prevData => {
        const updatedData = Object.keys(prevData).reduce((acc: any, key) => {
          acc[key] = {
            ...prevData[key],
            value: customerData[key] || prevData[key].value
          };
          return acc;
        }, {});
        return updatedData;
      });
    }
  }, [getCustomerQuery.isSuccess, getCustomerQuery.data]);

  const handleSubmit = (event: FormEvent) => {
    const form = event.currentTarget as HTMLFormElement;

    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    } else {
      return runFormSubmissionAction(event);
    }

    setValidated(true);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setCreateCustomerDto(prevData => ({
      ...prevData,
      [name]: {
        ...prevData[name],
        value
      }
    }));
  };

  const runFormSubmissionAction = async (e: FormEvent) => {
    e.preventDefault();

    const {
      organizationName: { value: organizationName },
      address: { value: address },
      state: { value: state },
      country: { value: country },
      companyLogoUrl: { value: companyLogoUrl },
      phoneNumber: { value: phoneNumber }
    } = createCustomerDto;
    const payload: CreateCustomerDTO = {
      ...createCustomerDto,
      organizationName,
      address,
      state,
      country,
      companyLogoUrl,
      phoneNumber
    };

    await updateCustomerAction.mutateAsync({
      customerId: id,
      formData: payload
    });
  };

  const renderFormFields = () => {
    return Object.entries(createCustomerDto).map(([key, formProps]) => (
      <Col md="6" key={key}>
        <CreateStaffForm
          formProps={formProps as unknown as FormProps}
          handleInputChange={handleInputChange}
        />
      </Col>
    ));
  };

  return (
    <div className="mb-9">
      <PageBreadcrumb items={CustomerEditBreadcrumbItems} />
      <div className="border-bottom mb-7 mx-n3 px-2 mx-lg-n6 px-lg-6">
        <Row>
          <Col xl={9}>
            <div className="d-sm-flex justify-content-between">
              <h2 className="mb-4">Edit Company</h2>
              <div className="d-flex mb-3">
                <Link
                  to={`${SITE_ENDPOINT.BASE}${SITE_ENDPOINT.COMPANY.INDEX}`}
                >
                  <Button variant="phoenix-primary" className="me-2 px-6">
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      <Row>
        <Col xl={9}>
          {getCustomerQuery.isLoading && <PhoenixLoader />}
          {getCustomerQuery.isError && (
            <Alert variant="danger">
              Error loading customer data. Please try again later.
            </Alert>
          )}
          {getCustomerQuery.isSuccess && (
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
              <h4 className="mb-3">Company Information</h4>
              <Row className="g-3 mb-8">{renderFormFields()}</Row>
              <div className="text-end">
                <Button
                  variant="primary"
                  className="mt-6"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Company'}
                </Button>
              </div>
            </Form>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default EditCustomer;
